import { z } from 'zod';
import type { BaseEntity } from './common';
import type { Worker } from './worker';
import type { CostCenter } from './costCenter';

/**
 * Enum per i tipi di contratto
 * Corrisponde al EmploymentType del backend
 */
export enum EmploymentType {
	FullTime = 1,
	PartTimeHorizontal = 2,
	PartTimeVertical = 3,
}

/**
 * WorkingRelationship - Rapporto di lavoro
 * Rappresenta il contratto di lavoro di un lavoratore con date, ore e costi
 * Corrisponde al modello backend WorkingRelationship
 */
export interface WorkingRelationship extends BaseEntity {
	/** Data di inizio contratto */
	startDate: string; // ISO date string (DateTime nel backend)
	/** Data di fine contratto (opzionale) */
	endDate?: string | null; // ISO date string (DateTime? nel backend)
	/** Costo giornaliero FTE (decimal 18,4) */
	fteDailyCost: number;
	/** Tipo di contratto interno */
	employmentType: EmploymentType;
	/** Tipo numerico per compatibilità backend */
	type: number;
	/** Ore per giorno - usato da PartTimeVertical (decimal 18,4) */
	hourPerDay?: number | null;
	/** Ore lunedì - usato da PartTimeHorizontal (decimal 18,4) */
	mondayHours?: number | null;
	/** Ore martedì - usato da PartTimeHorizontal (decimal 18,4) */
	tuesdayHours?: number | null;
	/** Ore mercoledì - usato da PartTimeHorizontal (decimal 18,4) */
	wednesdayHours?: number | null;
	/** Ore giovedì - usato da PartTimeHorizontal (decimal 18,4) */
	thursdayHours?: number | null;
	/** Ore venerdì - usato da PartTimeHorizontal (decimal 18,4) */
	fridayHours?: number | null;
	/** Ore sabato - usato da PartTimeHorizontal (decimal 18,4) */
	saturdayHours?: number | null;
	/** Ore domenica - usato da PartTimeHorizontal (decimal 18,4) */
	sundayHours?: number | null;
	/** ID del lavoratore */
	workerId: number;
	/** Lavoratore associato (navigation property, popolata con $expand=worker) */
	worker?: Worker;
	/** ID del centro di costo (opzionale) */
	costCenterId?: number | null;
	/** Centro di costo associato (navigation property, popolata con $expand=costCenter) */
	costCenter?: CostCenter;
	/** Note sul contratto (opzionale) */
	notes?: string | null;
}

/**
 * Schema Zod per la validazione runtime dei WorkingRelationship
 */
export const WorkingRelationshipSchema = z.object({
	id: z.number(),
	startDate: z.string(),
	endDate: z.string().nullish(), // Può essere null dal backend
	fteDailyCost: z.number(),
	employmentType: z.nativeEnum(EmploymentType).optional(), // Potrebbe non essere sempre presente
	type: z.number(),
	hourPerDay: z.number().nullish(), // Può essere null dal backend
	mondayHours: z.number().nullish(), // Può essere null dal backend
	tuesdayHours: z.number().nullish(), // Può essere null dal backend
	wednesdayHours: z.number().nullish(), // Può essere null dal backend
	thursdayHours: z.number().nullish(), // Può essere null dal backend
	fridayHours: z.number().nullish(), // Può essere null dal backend
	saturdayHours: z.number().nullish(), // Può essere null dal backend
	sundayHours: z.number().nullish(), // Può essere null dal backend
	workerId: z.number(),
	costCenterId: z.number().nullish(),
	notes: z.string().nullish(),
	// Campi audit da BaseEntity
	createdAt: z.string(),
	modifiedAt: z.string().optional(),
	createdBy: z.string(),
	modifiedBy: z.string().optional(),
	// Navigation properties opzionali (non validate in runtime)
	worker: z.any().optional(),
	costCenter: z.any().optional(),
});
